import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Bundle of media handles whose creation must be co-located with the user
 * gesture that grants camera + microphone access. iOS Safari binds the
 * AudioContext "running" state and the HTMLAudioElement autoplay grant to
 * the originating gesture; creating either of them later in a different tick
 * leaves the context suspended and the audio element unable to start.
 */
export interface CameraGrant {
  stream: MediaStream
  /** Resumed AudioContext, ready to receive a MediaStreamSourceNode. */
  audioContext: AudioContext
  /**
   * A detached HTMLAudioElement that was primed (.play() called on a silent
   * 1-frame WAV) inside the granting gesture. Setting `el.src = ...` and
   * calling `el.play()` on this same instance afterwards is allowed by iOS
   * without a fresh gesture.
   */
  audioElement: HTMLAudioElement
}

export type GrantErrorKind = 'permission-denied' | 'unsupported' | 'unknown'

export interface GrantError {
  kind: GrantErrorKind
  message: string
}

/**
 * A 44-byte WAV header followed by a single silent PCM sample. Tiny enough
 * to inline as a data: URL; valid enough that iOS's MEDIA_ELEMENT_SOURCE
 * code path treats `.play()` as a real play and grants the autoplay token
 * to the element instance.
 */
const SILENT_WAV_DATA_URL =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='

const classifyError = (e: unknown): GrantError => {
  const err = e as { name?: string; message?: string }
  const message = err.message ?? String(e)
  if (err.name === 'NotAllowedError' || err.name === 'SecurityError' || /permission|denied/i.test(message)) {
    return { kind: 'permission-denied', message }
  }
  if (err.name === 'NotFoundError' || err.name === 'NotSupportedError' || /not supported|no device/i.test(message)) {
    return { kind: 'unsupported', message }
  }
  return { kind: 'unknown', message }
}

export interface UseRequestCameraGrantResult {
  requesting: boolean
  lastError: GrantError | null
  /** Runs synchronously inside the click handler — do not wrap in setTimeout/etc. */
  request: () => Promise<CameraGrant | null>
  clearError: () => void
}

/**
 * Hook that exposes a camera-grant request. The `request` function MUST be
 * called directly inside a user gesture (click handler) so the AudioContext
 * and HTMLAudioElement gesture grants bind on iOS.
 *
 * On success, returns the grant; on failure, sets `lastError` and returns
 * null. The hook does NOT own the grant's lifetime — the caller (a screen
 * component) holds it in state and uses `useGrantLifetime` to clean it up.
 */
export const useRequestCameraGrant = (): UseRequestCameraGrantResult => {
  const [requesting, setRequesting] = useState(false)
  const [lastError, setLastError] = useState<GrantError | null>(null)

  const request = useCallback(async (): Promise<CameraGrant | null> => {
    if (requesting) return null
    setRequesting(true)
    setLastError(null)

    const AudioCtxCtor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtxCtor) {
      const err: GrantError = { kind: 'unsupported', message: 'This browser does not support the Web Audio API.' }
      setLastError(err)
      setRequesting(false)
      return null
    }
    const audioContext = new AudioCtxCtor()
    // resume() returns a Promise but iOS treats the synchronous call as the
    // gesture-binding event. Don't await it — awaiting can hang on iOS.
    void audioContext.resume().catch(() => { /* surface via LiveSession if it stays suspended */ })

    const audioElement = new Audio()
    audioElement.preload = 'auto'
    audioElement.src = SILENT_WAV_DATA_URL
    void audioElement.play().catch(() => { /* iOS occasionally throws; grant is granted regardless */ })

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: true,
      })
      setRequesting(false)
      return { stream, audioContext, audioElement }
    } catch (e) {
      try { void audioContext.close() } catch { /* */ }
      const err = classifyError(e)
      setLastError(err)
      setRequesting(false)
      return null
    }
  }, [requesting])

  const clearError = useCallback((): void => setLastError(null), [])

  return { requesting, lastError, request, clearError }
}

/**
 * Tear down a grant when the holder unmounts. Stops the MediaStream tracks
 * (releasing the camera + mic indicator) and closes the AudioContext. Safe
 * to call with `null` — no-op until a grant is set. The LiveSession that
 * borrows these handles is expected to be closed first (it disconnects its
 * own nodes); this hook does the source-of-truth cleanup.
 */
export const useGrantLifetime = (grant: CameraGrant | null): void => {
  // Track the most recent grant in a ref so the cleanup-only effect can
  // tear down whatever was last set when the component unmounts.
  const grantRef = useRef<CameraGrant | null>(grant)
  useEffect(() => { grantRef.current = grant }, [grant])

  useEffect(() => {
    return () => {
      const g = grantRef.current
      if (!g) return
      for (const t of g.stream.getTracks()) {
        try { t.stop() } catch { /* */ }
      }
      try { void g.audioContext.close() } catch { /* */ }
    }
  }, [])
}
